import React, { useEffect, useState } from 'react';
import { Portal, IconButton, Tooltip } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import {
    CallMergeRounded as MergeAllIcon,
    InsertDriveFileOutlined as OnePerFileIcon,
    AutoStoriesOutlined as OnePerPdfPageIcon,
    AutoAwesomeOutlined as SmartSplitIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Constants from '../../../assests/Constants/constants';
import styles from './GroupingModeModal.module.scss';

const MODE_META = {
    MergeAll: { icon: MergeAllIcon },
    OnePerFile: { icon: OnePerFileIcon },
    OnePerPdfPage: { icon: OnePerPdfPageIcon },
    SmartSplit: { icon: SmartSplitIcon, isNew: true },
};

export const ALL_GROUPING_MODE_KEYS = ['MergeAll', 'OnePerFile', 'OnePerPdfPage', 'SmartSplit'];
export const CLASSIC_GROUPING_MODE_KEYS = ['MergeAll', 'OnePerFile', 'OnePerPdfPage'];

/** Mode key ('OnePerFile', ...) for a Constants.InvoiceGroupingMode value. */
export const groupingModeKeyOf = (value) =>
    Object.keys(Constants.InvoiceGroupingMode).find((k) => Constants.InvoiceGroupingMode[k] === value);

/**
 * Picks how uploaded files / PDF pages are grouped into documents.
 *
 * @param {string[]} modes      mode keys to offer, in order (see ALL_GROUPING_MODE_KEYS)
 * @param {(value: number, key: string) => void} onSelect  called on Continue (or double-click)
 */
const GroupingModeModal = ({
    open,
    onClose,
    onSelect,
    modes = CLASSIC_GROUPING_MODE_KEYS,
    direction: directionProp,
}) => {
    const { t, i18n } = useTranslation();
    const direction = directionProp || i18n.dir?.() || 'rtl';
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        if (open) setSelected(null);
    }, [open]);

    if (!open) return null;

    const confirm = (key) => {
        if (!key) return;
        onSelect?.(Constants.InvoiceGroupingMode[key], key);
    };

    return (
        <Portal>
            <div
                className={`${styles.overlay} ${direction === 'rtl' ? styles.rtl : styles.ltr}`}
                onClick={onClose}
            >
                <div
                    className={styles.modal}
                    dir={direction}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="grouping-mode-title"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className={styles.body}>
                        <div className={styles.header}>
                            <Tooltip title={t('Close') || 'Close'}>
                                <IconButton onClick={onClose} className={styles.closeBtn}>
                                    <CancelIcon className={styles.closeIcon} />
                                </IconButton>
                            </Tooltip>
                            <div id="grouping-mode-title">{t('MultipleFilesDetected')}</div>
                            <div className={styles.headerSpacer} />
                        </div>
                        <p className={styles.subtitle}>{t('SelectUploadGroupingMode')}</p>

                        <div className={styles.options} role="radiogroup">
                            {modes.map((key) => {
                                const meta = MODE_META[key];
                                if (!meta) return null;
                                const Icon = meta.icon;
                                const isSelected = selected === key;
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        role="radio"
                                        aria-checked={isSelected}
                                        className={[
                                            styles.option,
                                            meta.isNew ? styles.optionFeatured : '',
                                            isSelected ? styles.optionSelected : '',
                                        ].join(' ')}
                                        onClick={() => setSelected(key)}
                                        onDoubleClick={() => confirm(key)}
                                    >
                                        <span className={styles.optionIcon}>
                                            <Icon fontSize="small" />
                                        </span>
                                        <span className={styles.optionText}>
                                            <span className={styles.optionTitle}>
                                                {t(key)}
                                                {meta.isNew && <span className={styles.badge}>{t('New') || 'New'}</span>}
                                            </span>
                                            <span className={styles.optionDesc}>{t(`${key}Description`)}</span>
                                        </span>
                                        <span className={styles.radio} aria-hidden="true" />
                                    </button>
                                );
                            })}
                        </div>

                        <div className={styles.footer}>
                            <button type="button" className={styles.secondaryBtn} onClick={onClose}>
                                {t('cancelButton') || 'Cancel'}
                            </button>
                            <button
                                type="button"
                                className={styles.primaryBtn}
                                disabled={!selected}
                                onClick={() => confirm(selected)}
                            >
                                {t('Continue') || 'Continue'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default GroupingModeModal;
